import React, { useMemo } from 'react';
import { Offcanvas, Card, Row, Col, Table, Badge, Button, Spinner } from 'react-bootstrap';
import dayjs from 'dayjs';
import { 
    FaCheckCircle, 
    FaInfoCircle, 
    FaClock, 
    FaMoneyBillWave, 
    FaFileAlt, 
    FaFileInvoice, 
    FaDownload 
} from 'react-icons/fa';

export const formatUSAmount = (amount) => {
    if (amount === null || amount === undefined || amount === '') return '0.00';
    const num = parseFloat(amount);
    if (isNaN(num)) return '0.00';
    return num.toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    });
};

export const parseAmount = (amount) => {
    if (amount === null || amount === undefined || amount === '') return 0;
    const parsed = parseFloat(amount);
    return isNaN(parsed) ? 0 : parsed;
};

export const calculateInvoiceTotals = (invoice, type = 'proforma') => {
    if (!invoice) return { invoiceAmount: 0, vatAmount: 0, invoiceTotalWithVat: 0, invoicePaidAmount: 0, invoiceBalance: 0, isFullyPaid: false, currency: 'AED' };

    let invoiceType = type;
    const isTaxInvoice =
        type === 'tax' ||
        invoice.tax_invoice_generated === true ||
        invoice.tax_invoice_generated === "1" ||
        invoice.tax_invoice_generated === 1 ||
        invoice.proforma_invoice ||
        (invoice.ref_no && (String(invoice.ref_no).includes('TI/') || String(invoice.ref_no).includes('/TI/')));

    if (isTaxInvoice) {
        invoiceType = 'tax';
    }

    let invoiceAmount = 0;
    let vatAmount = 0;
    let invoiceTotalWithVat = 0;
    let invoicePaidAmount = 0;

    if (invoiceType === 'proforma') {
        invoiceAmount = parseAmount(invoice.basic_amount || invoice.amount || (parseAmount(invoice.total_with_vat) - parseAmount(invoice.vat_amount)));
        vatAmount = parseAmount(invoice.vat_amount);
        invoiceTotalWithVat = parseAmount(invoice.total_with_vat || (invoiceAmount + vatAmount));
    } else {
        invoiceAmount = parseAmount(invoice.basic_amount || invoice.amount || invoice.total_amount || (parseAmount(invoice.total_with_vat) - parseAmount(invoice.vat_amount)));
        vatAmount = parseAmount(invoice.vat_amount);
        invoiceTotalWithVat = parseAmount(invoice.total_with_vat || (invoiceAmount + vatAmount));
    }

    const targetInvoiceId = (invoiceType === 'tax' && invoice.proforma_invoice)
        ? Number(invoice.proforma_invoice.id || invoice.pi_id)
        : Number(invoice.id);

    let transactionItems = (invoiceType === 'tax' && invoice.proforma_invoice)
        ? (invoice.proforma_invoice.transaction_invoice_items || [])
        : (invoice.transaction_invoice_items || []);

    if ((!transactionItems || transactionItems.length === 0) && invoice.proforma_invoice && invoice.proforma_invoice.transaction_invoice_items) {
        transactionItems = invoice.proforma_invoice.transaction_invoice_items;
    }

    if (transactionItems && Array.isArray(transactionItems)) {
        const itemsPaid = transactionItems.reduce(
            (sum, item) => {
                if (item.invoice_id && targetInvoiceId && Number(item.invoice_id) !== targetInvoiceId) {
                    return sum;
                }
                return sum + parseAmount(item.paid_amount);
            }, 0
        );
        if (itemsPaid > 0) {
            invoicePaidAmount = itemsPaid;
        }
    }

    const activePivot = (invoiceType === 'tax' && invoice.proforma_invoice)
        ? invoice.proforma_invoice.pivot
        : invoice.pivot;

    if (activePivot && activePivot.allocated_amount) {
        const pivotAmount = parseAmount(activePivot.allocated_amount);
        if (pivotAmount > invoicePaidAmount) {
            invoicePaidAmount = pivotAmount;
        }
    }

    const activeTransactions = (invoiceType === 'tax' && invoice.proforma_invoice)
        ? (invoice.proforma_invoice.transactions || [])
        : (invoice.transactions || []);

    if (activeTransactions && Array.isArray(activeTransactions)) {
        const transactionPaidAmount = activeTransactions.reduce(
            (sum, trans) => {
                if (trans.pivot && trans.pivot.allocated_amount) {
                    return sum + parseAmount(trans.pivot.allocated_amount);
                }
                return sum;
            }, 0
        );
        if (transactionPaidAmount > 0 && transactionPaidAmount > invoicePaidAmount) {
            invoicePaidAmount = transactionPaidAmount;
        }
    }

    if (invoiceType === 'tax' && invoice.proforma_invoice && invoicePaidAmount === 0) {
        const proforma = invoice.proforma_invoice;
        if (proforma.pi_status === 'payment_received' || proforma.pi_status === 'paid') {
            invoicePaidAmount = invoiceTotalWithVat;
        }
    }

    const invoiceBalance = parseFloat((invoiceTotalWithVat - invoicePaidAmount).toFixed(2));
    const isFullyPaid = Math.abs(invoiceBalance) < 0.01 || invoicePaidAmount >= invoiceTotalWithVat;

    return {
        invoiceAmount,
        vatAmount,
        invoiceTotalWithVat,
        invoicePaidAmount,
        invoiceBalance,
        isFullyPaid,
        currency: invoice.currency || invoice.client?.currency || invoice.proforma_invoice?.client?.currency || invoice.proforma_invoice?.quotation?.project?.client?.currency || invoice.quotation?.project?.client?.currency || 'AED',
        pi_status: invoice.pi_status || invoice.proforma_invoice?.pi_status,
        invoiceType: invoiceType,
        quotationTotal: parseAmount(invoice.quotation?.total_amount || invoice.proforma_invoice?.quotation?.total_amount || 0),
        quotationTotalWithVat: parseAmount(invoice.quotation?.total_with_vat || invoice.proforma_invoice?.quotation?.total_with_vat || 0),
        thisInvoiceAmount: invoiceTotalWithVat
    };
};

export const processInvoiceDetails = (invoice, type = 'tax') => {
    if (!invoice) return { invoiceWithPaymentData: null, paymentHistory: [] };

    let invoiceType = type;
    if (type === 'tax' || invoice.tax_invoice_generated === true ||
        invoice.tax_invoice_generated === 1 || invoice.proforma_invoice || (invoice.ref_no && String(invoice.ref_no).includes('TI'))) {
        invoiceType = 'tax';
    }

    let paymentHistory = [];
    let transactionInvoiceItems = [];
    let transactionAllocations = [];

    const targetInvoiceId = (invoiceType === 'tax' && invoice.proforma_invoice)
        ? Number(invoice.proforma_invoice.id || invoice.pi_id)
        : Number(invoice.id);

    // Fallback for Tax Invoices: strictly use Proforma Invoice's payments if available
    if (invoiceType === 'tax' && invoice.proforma_invoice) {
        invoice.transactions = invoice.proforma_invoice.transactions || [];
        invoice.transaction_invoice_items = invoice.proforma_invoice.transaction_invoice_items || [];
        invoice.pivot = invoice.proforma_invoice.pivot || null;
    }

    // Process pivot data from transaction relationships
    if (invoice.pivot && invoice.pivot.allocated_amount) {
        transactionInvoiceItems.push({
            id: `pivot-${invoice.id}`,
            paid_amount: invoice.pivot.allocated_amount,
            balance_amount: parseAmount(invoice.total_with_vat) - parseAmount(invoice.pivot.allocated_amount),
            is_fully_paid: parseAmount(invoice.pivot.allocated_amount) >= parseAmount(invoice.total_with_vat)
        });
    }

    const getVoucherId = (t, item) => {
        return (
            item?.transaction_voucher_id ||
            t?.transaction_voucher_id ||
            t?.voucher_id ||
            t?.voucher_no ||
            item?.voucher_id ||
            item?.transaction_reference ||
            t?.reference_no ||
            (t?.id ? `TV-${t.id}` : (item?.transaction_id ? `TV-${item.transaction_id}` : (item?.id ? `TV-${item.id}` : 'N/A')))
        );
    };

    // Process transactions array with detailed allocation
    if (invoice.transactions && Array.isArray(invoice.transactions)) {
        invoice.transactions.forEach(transaction => {
            const voucherId = getVoucherId(transaction);
            const txnTotalAmount = parseAmount(transaction.amount || transaction.total_bank_amount || transaction.base_amount || 0);
            const accountName = transaction?.account?.name || transaction?.bank_name || transaction?.account_name || (transaction?.account?.type ? transaction.account.type : '');
            
            const transactionData = {
                id: transaction.id,
                transactionId: transaction.id,
                date: transaction.date || transaction.created_at,
                voucherId: voucherId,
                voucher_id: voucherId,
                reference_no: voucherId,
                transaction_voucher_id: voucherId,
                totalTransactionAmount: txnTotalAmount,
                total_amount: txnTotalAmount,
                accountName: accountName,
                payment_method: transaction.account?.type || transaction.payment_mode || 'Bank Transfer',
                status: transaction.reconciliation_status === 'reconciled' ? 'Completed' : 'Pending',
                account_details: transaction.account ? {
                    account_name: transaction.account.name,
                    account_number: transaction.account.account_number,
                    bank_name: transaction.account.branch_name
                } : null,
                allocated_to_invoices: []
            };

            if (transaction.pivot && transaction.pivot.allocated_amount) {
                const allocatedAmount = parseAmount(transaction.pivot.allocated_amount);
                transactionData.allocated_to_invoices.push({
                    invoice_ref: invoice.ref_no,
                    allocated_amount: allocatedAmount,
                    amount: allocatedAmount,
                    percentage: txnTotalAmount > 0 ? (allocatedAmount / txnTotalAmount) * 100 : 100
                });

                paymentHistory.push({
                    ...transactionData,
                    key: `payment-pivot-${transaction.id}-${invoice.id}`,
                    amount: allocatedAmount,
                    remainingBalance: parseAmount(transaction.pivot.balance_amount ?? 0),
                    remaining_balance: parseAmount(transaction.pivot.balance_amount ?? 0),
                    is_fully_paid: parseAmount(transaction.pivot.allocated_amount) >= parseAmount(invoice.total_with_vat),
                    note: `Allocated ${formatUSAmount(allocatedAmount)} from total ${formatUSAmount(txnTotalAmount)}`
                });
            }

            if (transaction.transaction_invoice_items && Array.isArray(transaction.transaction_invoice_items)) {
                transaction.transaction_invoice_items.forEach(item => {
                    if (targetInvoiceId && Number(item.invoice_id) === targetInvoiceId) {
                        const allocatedAmount = parseAmount(item.paid_amount);
                        const itemVoucherId = getVoucherId(transaction, item);
                        transactionData.allocated_to_invoices.push({
                            invoice_ref: invoice.ref_no,
                            allocated_amount: allocatedAmount,
                            amount: allocatedAmount,
                            invoice_id: item.invoice_id,
                            transaction_invoice_item_id: item.id
                        });
                        paymentHistory.push({
                            ...transactionData,
                            key: `payment-item-${item.id || transaction.id}-${invoice.id}`,
                            id: item.id || transaction.id,
                            transactionId: transaction.id,
                            voucherId: itemVoucherId,
                            voucher_id: itemVoucherId,
                            reference_no: itemVoucherId,
                            transaction_voucher_id: itemVoucherId,
                            amount: allocatedAmount,
                            remainingBalance: parseAmount(item.balance_amount ?? 0),
                            remaining_balance: parseAmount(item.balance_amount ?? 0),
                            is_fully_paid: item.is_fully_paid,
                            note: `Allocated ${formatUSAmount(allocatedAmount)} from total ${formatUSAmount(txnTotalAmount)}`
                        });
                    }
                });
            }

            if (transactionData.allocated_to_invoices.length > 0) {
                transactionAllocations.push(transactionData);
            }
        });
    }

    // Process transaction_invoice_items if available
    if (invoice.transaction_invoice_items && Array.isArray(invoice.transaction_invoice_items)) {
        invoice.transaction_invoice_items.forEach(item => {
            if (item.invoice_id && targetInvoiceId && Number(item.invoice_id) !== targetInvoiceId) {
                return;
            }
            const tr = item.transaction || {};
            const voucherId = getVoucherId(tr, item);
            const txnTotalAmount = parseAmount(tr.amount || item.amount || 0);
            const accountName = tr?.account?.name || tr?.bank_name || tr?.account_name || (tr?.account?.type ? tr.account.type : '');
            
            transactionInvoiceItems.push({
                id: item.id,
                paid_amount: item.paid_amount,
                balance_amount: item.balance_amount,
                is_fully_paid: item.is_fully_paid,
                transaction_id: item.transaction_id,
                transaction_reference: voucherId,
                transaction_voucher_id: voucherId,
                voucherId: voucherId,
                transaction_date: tr.date || tr.created_at || item.transaction_date
            });

            paymentHistory.push({
                key: `payment-tii-${item.id}-${invoice.id}`,
                id: item.id,
                transactionId: tr.id || item.transaction_id,
                date: tr.date || tr.created_at || item.transaction_date,
                payment_date: tr.date || tr.created_at || item.transaction_date,
                voucherId: voucherId,
                voucher_id: voucherId,
                reference_no: voucherId,
                transaction_voucher_id: voucherId,
                amount: parseAmount(item.paid_amount),
                totalTransactionAmount: txnTotalAmount,
                original_amount: txnTotalAmount,
                remainingBalance: parseAmount(item.balance_amount ?? 0),
                remaining_balance: parseAmount(item.balance_amount ?? 0),
                is_fully_paid: item.is_fully_paid,
                accountName: accountName,
                payment_method: tr.account?.type || tr.payment_mode || 'Bank Transfer',
                status: tr.reconciliation_status === 'reconciled' ? 'Completed' : (item.is_fully_paid ? 'Completed' : 'Pending'),
                account_details: tr.account ? {
                    account_name: tr.account.name,
                    account_number: tr.account.account_number,
                    bank_name: tr.account.branch_name
                } : null,
                note: `Payment of ${formatUSAmount(item.paid_amount)} applied to this invoice`
            });
        });
    }

    // Fallback if paid amount exists directly on invoice
    if (paymentHistory.length === 0 && parseAmount(invoice.amount_received || invoice.paid_amount) > 0) {
        const voucherId = getVoucherId(null, invoice);
        const paidAmt = parseAmount(invoice.amount_received || invoice.paid_amount);
        const balAmt = parseAmount(invoice.balance !== undefined ? invoice.balance : (parseAmount(invoice.total_with_vat) - paidAmt));
        paymentHistory.push({
            key: `payment-rec-${invoice.id}`,
            id: `received-${invoice.id}`,
            date: invoice.payment_date || invoice.updated_at || invoice.date,
            voucherId: voucherId !== 'N/A' ? voucherId : (invoice.transaction_voucher_id || 'Payment Received'),
            voucher_id: voucherId !== 'N/A' ? voucherId : (invoice.transaction_voucher_id || 'Payment Received'),
            reference_no: voucherId !== 'N/A' ? voucherId : (invoice.transaction_voucher_id || 'Payment Received'),
            transaction_voucher_id: voucherId !== 'N/A' ? voucherId : (invoice.transaction_voucher_id || 'Payment Received'),
            amount: paidAmt,
            remainingBalance: balAmt,
            remaining_balance: balAmt,
            accountName: invoice.account_name || invoice.bank_name || '',
            is_fully_paid: balAmt <= 0
        });
    }

    // Deduplicate payment history
    const uniquePaymentHistory = [];
    const seen = new Set();
    for (const p of paymentHistory) {
        const vKey = p.voucherId || p.voucher_id || p.transaction_voucher_id || p.transactionId || p.id;
        const key = `${vKey}_${p.amount}`;
        if (!seen.has(key)) {
            seen.add(key);
            uniquePaymentHistory.push(p);
        }
    }

    const invoiceWithPaymentData = {
        ...invoice,
        ...(transactionInvoiceItems.length > 0 && {
            transaction_invoice_items: transactionInvoiceItems
        }),
        ...(transactionAllocations.length > 0 && {
            transaction_allocations: transactionAllocations
        }),
        ...(invoice.pivot && { pivot: invoice.pivot }),
        pi_status: invoice.pi_status || invoice.proforma_invoice?.pi_status,
        invoiceType: invoiceType
    };

    return { invoiceWithPaymentData, paymentHistory: uniquePaymentHistory };
};

const InvoiceDetailsOffcanvas = ({ show, onHide, invoice, paymentHistory: propPaymentHistory }) => {
    const { selectedInvoiceDetails, invoicePaymentHistory } = useMemo(() => {
        if (!invoice) return { selectedInvoiceDetails: null, invoicePaymentHistory: [] };
        
        // If already processed
        if (propPaymentHistory !== undefined) {
            return {
                selectedInvoiceDetails: invoice,
                invoicePaymentHistory: propPaymentHistory || []
            };
        }

        const res = processInvoiceDetails(invoice, invoice.invoiceType || (invoice.tax_invoice || (invoice.ref_no && String(invoice.ref_no).includes('TI')) ? 'tax' : 'proforma'));
        return {
            selectedInvoiceDetails: res.invoiceWithPaymentData,
            invoicePaymentHistory: res.paymentHistory
        };
    }, [invoice, propPaymentHistory]);

    const backendUrl = process.env.REACT_APP_BACKEND_URL || '';

    return (
        <Offcanvas
            show={show}
            onHide={onHide}
            placement="end"
            style={{ width: '800px' }}
            className="invoice-details-offcanvas shadow-lg"
        >
            <Offcanvas.Header closeButton className="border-bottom">
                <Offcanvas.Title className="w-100">
                    <div className="d-flex justify-content-between align-items-center">
                        <div>
                            <h5 className="mb-0">Invoice Details</h5>
                            {selectedInvoiceDetails && (
                                <small className="text-muted">
                                    {selectedInvoiceDetails.ref_no}
                                    {(selectedInvoiceDetails.pi_status === 'payment_received' || selectedInvoiceDetails.pi_status === 'paid') &&
                                        <span className="ms-2">
                                            <FaCheckCircle className="text-success" />
                                        </span>
                                    }
                                </small>
                            )}
                        </div>
                        {selectedInvoiceDetails && (
                            <div className="d-flex align-items-center gap-2">
                                {(selectedInvoiceDetails.tax_invoice_generated === "1" || selectedInvoiceDetails.tax_invoice_generated === true || selectedInvoiceDetails.tax_invoice || (selectedInvoiceDetails.ref_no && String(selectedInvoiceDetails.ref_no).includes('TI'))) ? (
                                    <Badge bg="success" className="fs-6">
                                        <FaCheckCircle className="me-1" /> Tax Invoice Generated
                                    </Badge>
                                ) : null}
                                {(selectedInvoiceDetails.pi_status === 'payment_received' || selectedInvoiceDetails.pi_status === 'paid') && (
                                    <Badge bg="success" className="fs-6">
                                        <FaCheckCircle className="me-1" /> Paid
                                    </Badge>
                                )}
                            </div>
                        )}
                    </div>
                </Offcanvas.Title>
            </Offcanvas.Header>

            <Offcanvas.Body>
                {selectedInvoiceDetails ? (
                    <>
                        {/* Basic Information Card */}
                        <Card className="mb-4">
                            <Card.Header className="bg-light">
                                <h6 className="mb-0">Basic Information</h6>
                            </Card.Header>
                            <Card.Body>
                                <Row className="mb-2">
                                    <Col sm={4}><strong>Invoice Ref:</strong></Col>
                                    <Col sm={8}>{selectedInvoiceDetails.ref_no}</Col>
                                </Row>
                                <Row className="mb-2">
                                    <Col sm={4}><strong>Invoice Date:</strong></Col>
                                    <Col sm={8}>
                                        {dayjs(
                                            selectedInvoiceDetails.invoice_date ||
                                            selectedInvoiceDetails.date ||
                                            selectedInvoiceDetails.created_at
                                        ).format('DD-MM-YYYY')}
                                    </Col>
                                </Row>
                                <Row className="mb-2">
                                    <Col sm={4}><strong>Client:</strong></Col>
                                    <Col sm={8}>
                                        {selectedInvoiceDetails.client?.name ||
                                            selectedInvoiceDetails.client?.client_name ||
                                            selectedInvoiceDetails.client_name ||
                                            selectedInvoiceDetails.quotation?.project?.client?.name ||
                                            selectedInvoiceDetails.proforma_invoice?.quotation?.project?.client?.name ||
                                            selectedInvoiceDetails.proforma_invoice?.client?.name ||
                                            selectedInvoiceDetails.proforma_invoice?.client?.client_name ||
                                            'N/A'}
                                    </Col>
                                </Row>
                                <Row className="mb-2">
                                    <Col sm={4}><strong>Project:</strong></Col>
                                    <Col sm={8}>
                                        {selectedInvoiceDetails.project?.name ||
                                            selectedInvoiceDetails.project?.project_name ||
                                            selectedInvoiceDetails.project_name ||
                                            selectedInvoiceDetails.quotation?.project?.name ||
                                            selectedInvoiceDetails.proforma_invoice?.quotation?.project?.name ||
                                            selectedInvoiceDetails.proforma_invoice?.project?.name ||
                                            selectedInvoiceDetails.proforma_invoice?.project?.project_name ||
                                            'N/A'}
                                    </Col>
                                </Row>
                                <Row className="mb-2">
                                    <Col sm={4}><strong>Quotation Ref:</strong></Col>
                                    <Col sm={8}>
                                        {selectedInvoiceDetails.quotation?.ref_no ||
                                            selectedInvoiceDetails.proforma_invoice?.quotation?.ref_no ||
                                            'N/A'}
                                    </Col>
                                </Row>
                            </Card.Body>
                        </Card>

                        {/* Invoice Financial Summary */}
                        <Card className="mb-4">
                            <Card.Header className="bg-light">
                                <h6 className="mb-0">Invoice Financial Summary</h6>
                                {selectedInvoiceDetails?.transaction_invoice_items?.length > 0 && (
                                    <small className="text-muted d-block">
                                        <FaInfoCircle className="me-1" />
                                        Payment records available
                                    </small>
                                )}
                                {selectedInvoiceDetails?.pivot?.allocated_amount && (
                                    <small className="text-muted d-block">
                                        <FaInfoCircle className="me-1" />
                                        Payment allocated from transaction
                                    </small>
                                )}
                            </Card.Header>
                            <Card.Body>
                                {(() => {
                                    const type = selectedInvoiceDetails.invoiceType ||
                                        (selectedInvoiceDetails.tax_invoice ? 'tax' : 'proforma');
                                    const totals = calculateInvoiceTotals(selectedInvoiceDetails, type);

                                    return (
                                        <>
                                            {/* Invoice Breakdown */}
                                            <div className="mb-3">
                                                <Row className="mb-2">
                                                    <Col sm={8}>
                                                        <strong>Invoice Amount (excl. VAT):</strong>
                                                        <div className="small text-muted">
                                                            Basic amount before VAT
                                                        </div>
                                                    </Col>
                                                    <Col sm={4} className="text-end">
                                                        {formatUSAmount(totals.invoiceAmount)} {totals.currency}
                                                    </Col>
                                                </Row>

                                                <Row className="mb-2">
                                                    <Col sm={8}>
                                                        <strong>VAT Amount (5%):</strong>
                                                        <div className="small text-muted">
                                                            Tax calculated at 5%
                                                        </div>
                                                    </Col>
                                                    <Col sm={4} className="text-end">
                                                        {formatUSAmount(totals.vatAmount)} {totals.currency}
                                                    </Col>
                                                </Row>

                                                <hr />

                                                <Row className="mb-3">
                                                    <Col sm={8}>
                                                        <strong>Total Invoice Amount:</strong>
                                                        <div className="small text-muted">
                                                            Amount due including VAT
                                                        </div>
                                                    </Col>
                                                    <Col sm={4} className="text-end">
                                                        <h5 className="mb-0 text-primary">
                                                            {formatUSAmount(totals.invoiceTotalWithVat)} {totals.currency}
                                                        </h5>
                                                    </Col>
                                                </Row>
                                            </div>

                                            {/* Payment Status */}
                                            <div className="payment-status-section p-3 bg-light rounded">
                                                <h6 className="mb-3">Payment Status</h6>

                                                <Row className="mb-2">
                                                    <Col sm={8}>
                                                        <strong>Amount Paid:</strong>
                                                        {selectedInvoiceDetails?.paid_date && (
                                                            <div className="small text-muted">
                                                                Paid on: {dayjs(selectedInvoiceDetails.paid_date).format('DD-MM-YYYY')}
                                                            </div>
                                                        )}
                                                    </Col>
                                                    <Col sm={4} className="text-end">
                                                        <h5 className={`mb-0 ${totals.invoicePaidAmount > 0 ? 'text-success' : 'text-muted'}`}>
                                                            {formatUSAmount(totals.invoicePaidAmount)} {totals.currency}
                                                        </h5>
                                                        {totals.invoicePaidAmount > 0 && (
                                                            <div className="small">
                                                                {((totals.invoicePaidAmount / totals.invoiceTotalWithVat) * 100).toFixed(1)}% of total
                                                            </div>
                                                        )}
                                                    </Col>
                                                </Row>

                                                <Row className="mb-3">
                                                    <Col sm={8}>
                                                        <strong>Balance Due:</strong>
                                                        <div className="small text-muted">
                                                            Remaining amount to be paid
                                                        </div>
                                                    </Col>
                                                    <Col sm={4} className="text-end">
                                                        <h5 className={`mb-0 ${totals.invoiceBalance > 0 ? 'text-danger' :
                                                            totals.invoiceBalance < 0 ? 'text-warning' : 'text-success'
                                                            }`}>
                                                            {formatUSAmount(totals.invoiceBalance)} {totals.currency}
                                                        </h5>
                                                    </Col>
                                                </Row>

                                                {/* Status Badge */}
                                                <div className="text-center mt-3">
                                                    {totals.isFullyPaid ? (
                                                        <Badge bg="success" className="fs-6 p-2 px-4">
                                                            <FaCheckCircle className="me-2" />
                                                            <strong>PAID IN FULL</strong>
                                                        </Badge>
                                                    ) : totals.invoicePaidAmount > 0 ? (
                                                        <Badge bg="info" className="fs-6 p-2 px-4">
                                                            <FaInfoCircle className="me-2" />
                                                            <strong>PARTIALLY PAID</strong>
                                                        </Badge>
                                                    ) : (
                                                        <Badge bg="warning" className="fs-6 p-2 px-4">
                                                            <FaClock className="me-2" />
                                                            <strong>UNPAID</strong>
                                                        </Badge>
                                                    )}
                                                </div>
                                            </div>

                                            {/* Payment Details (Styled identical to ExpenseModal) */}
                                            <div className="card mt-4 border-0 shadow-sm">
                                                <div className="card-header bg-light">
                                                    <h6 className="mb-0">
                                                        <i className="mdi mdi-file-document-outline me-2"></i>
                                                        Payment Details
                                                    </h6>
                                                </div>
                                                <div className="card-body">
                                                    {invoicePaymentHistory.length > 0 ? (
                                                        invoicePaymentHistory.map((payment, idx) => (
                                                            <div key={payment.key || payment.id || idx} className="border rounded p-3 mb-3">
                                                                <div className="d-flex justify-content-between align-items-start">
                                                                    <div>
                                                                        <div className="fw-semibold text-dark">
                                                                            Voucher: {payment.voucherId || payment.voucher_id || payment.transaction_voucher_id || payment.reference_no}
                                                                        </div>
                                                                        <div className="text-muted small mt-1">
                                                                            Date: {payment.payment_date || payment.date ? dayjs(payment.payment_date || payment.date).format('DD-MM-YYYY') : 'N/A'}
                                                                        </div>
                                                                        {(payment.account_details?.account_name || payment.payment_method) && (
                                                                            <div className="text-muted small">
                                                                                Account: {payment.account_details?.account_name || payment.payment_method}
                                                                                {payment.account_details?.account_number && ` (${payment.account_details.account_number})`}
                                                                            </div>
                                                                        )}
                                                                        {payment.remaining_balance !== undefined && (
                                                                            <div className="text-muted small">
                                                                                Balance: {formatUSAmount(payment.remaining_balance)} {totals.currency}
                                                                            </div>
                                                                        )}
                                                                        {payment.original_amount && Number(payment.original_amount) > 0 && Number(payment.original_amount) !== Number(payment.amount) && (
                                                                            <div className="text-muted small mt-1">
                                                                                Total Transaction: <span className="fw-semibold text-dark">{formatUSAmount(payment.original_amount)} {totals.currency}</span>
                                                                            </div>
                                                                        )}
                                                                        {payment.allocated_to_invoices?.length > 1 && (
                                                                            <div className="mt-2 border-top pt-2">
                                                                                <small className="text-muted d-block mb-1 fw-medium text-uppercase" style={{ fontSize: '0.65rem', letterSpacing: '0.5px' }}>Combined Transaction Breakdown:</small>
                                                                                <div className="d-flex justify-content-between align-items-center mb-1 bg-light p-1 px-2 rounded">
                                                                                    <span className="small text-dark fw-medium">Payment for this Invoice:</span>
                                                                                    <span className="small fw-bold text-success">{formatUSAmount(payment.amount)} {totals.currency}</span>
                                                                                </div>
                                                                                {payment.allocated_to_invoices.filter(a => a.invoice_ref !== selectedInvoiceDetails.ref_no).map((alloc, aIdx) => (
                                                                                    <div key={aIdx} className="d-flex justify-content-between align-items-center mb-1 ps-2 ms-1 border-start">
                                                                                        <span className="text-muted" style={{ fontSize: '0.75rem' }}>{alloc.invoice_ref}</span>
                                                                                        <span className="text-muted fw-medium" style={{ fontSize: '0.75rem' }}>{formatUSAmount(alloc.allocated_amount)} {totals.currency}</span>
                                                                                    </div>
                                                                                ))}
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                    <div className="text-end">
                                                                        <div className="fw-bold text-success">
                                                                            {formatUSAmount(payment.amount)} {totals.currency}
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        ))
                                                    ) : (
                                                        <div className="text-muted">
                                                            No payment details found for this invoice yet.
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </>
                                    );
                                })()}
                            </Card.Body>
                        </Card>

                        {/* Quotation Summary */}
                        {(() => {
                            const type = selectedInvoiceDetails.tax_invoice ? 'tax' : 'proforma';
                            const totals = calculateInvoiceTotals(selectedInvoiceDetails, type);

                            if (totals.quotationTotal > 0) {
                                return (
                                    <Card className="mb-4">
                                        <Card.Header className="bg-light">
                                            <h6 className="mb-0 d-flex align-items-center">
                                                <FaFileAlt className="me-2" />
                                                Quotation Summary
                                            </h6>
                                        </Card.Header>
                                        <Card.Body>
                                            <Row className="mb-2">
                                                <Col sm={4}><strong>Quotation Ref:</strong></Col>
                                                <Col sm={8}>
                                                    {selectedInvoiceDetails.quotation?.ref_no || selectedInvoiceDetails.proforma_invoice?.quotation?.ref_no || 'N/A'}
                                                </Col>
                                            </Row>
                                            <Row className="mb-2">
                                                <Col sm={4}><strong>Quotation Total:</strong></Col>
                                                <Col sm={8}>
                                                    {formatUSAmount(totals.quotationTotal)} {totals.currency}
                                                </Col>
                                            </Row>
                                            <Row className="mb-2">
                                                <Col sm={4}><strong>This Invoice:</strong></Col>
                                                <Col sm={8}>
                                                    {formatUSAmount(totals.thisInvoiceAmount)} {totals.currency}
                                                    {totals.quotationTotalWithVat > 0 && (
                                                        <span className="text-muted small ms-2">
                                                            ({((totals.thisInvoiceAmount / totals.quotationTotalWithVat) * 100).toFixed(1)}% of quotation)
                                                        </span>
                                                    )}
                                                </Col>
                                            </Row>
                                        </Card.Body>
                                    </Card>
                                );
                            }
                            return null;
                        })()}

                        {/* Tax Invoice Information */}
                        {(selectedInvoiceDetails.tax_invoice ||
                            selectedInvoiceDetails.tax_invoice_generated === "1" ||
                            selectedInvoiceDetails.tax_invoice_generated === true) && (
                            <Card className="mb-4">
                                <Card.Header className="bg-light">
                                    <h6 className="mb-0 d-flex align-items-center">
                                        <FaFileInvoice className="me-2" />
                                        Tax Invoice Information
                                    </h6>
                                    {selectedInvoiceDetails.tax_invoice ? (
                                        <small className="text-muted">Tax Invoice Details</small>
                                    ) : (
                                        <small className="text-muted">Generated from Proforma Invoice</small>
                                    )}
                                </Card.Header>
                                <Card.Body>
                                    {selectedInvoiceDetails.tax_invoice ? (
                                        <>
                                            <Row className="mb-2">
                                                <Col sm={4}><strong>Tax Invoice Ref:</strong></Col>
                                                <Col sm={8}>
                                                    <strong>{selectedInvoiceDetails.tax_invoice.ref_no || 'N/A'}</strong>
                                                </Col>
                                            </Row>
                                            <Row className="mb-2">
                                                <Col sm={4}><strong>Tax Invoice Date:</strong></Col>
                                                <Col sm={8}>
                                                    {selectedInvoiceDetails.tax_invoice.date ?
                                                        dayjs(selectedInvoiceDetails.tax_invoice.date).format('DD-MM-YYYY') :
                                                        'N/A'}
                                                </Col>
                                            </Row>
                                            <Row className="mb-2">
                                                <Col sm={4}><strong>VAT Amount:</strong></Col>
                                                <Col sm={8}>
                                                    {formatUSAmount(selectedInvoiceDetails.tax_invoice.vat_amount || 0)}
                                                </Col>
                                            </Row>
                                            <Row className="mb-2">
                                                <Col sm={4}><strong>Total with VAT:</strong></Col>
                                                <Col sm={8}>
                                                    <strong>{formatUSAmount(selectedInvoiceDetails.tax_invoice.total_with_vat || 0)}</strong>
                                                </Col>
                                            </Row>
                                        </>
                                    ) : selectedInvoiceDetails.tax_invoice_generated === "1" || selectedInvoiceDetails.tax_invoice_generated === true ? (
                                        <div className="text-center py-3">
                                            <div className="mb-3">
                                                <FaCheckCircle className="text-success mb-2" size={48} />
                                                <h5>Tax Invoice Generated</h5>
                                                <p className="text-muted">
                                                    This proforma invoice has a corresponding tax invoice
                                                </p>
                                            </div>
                                            {selectedInvoiceDetails.tax_invoice_file && (
                                                <div className="mt-3">
                                                    <a
                                                        href={`${backendUrl}/storage/${selectedInvoiceDetails.tax_invoice_file}`}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="btn btn-sm btn-outline-primary"
                                                    >
                                                        <FaDownload className="me-1" />
                                                        Download Tax Invoice Document
                                                    </a>
                                                </div>
                                            )}
                                        </div>
                                    ) : null}
                                </Card.Body>
                            </Card>
                        )}
                    </>
                ) : (
                    <div className="text-center py-5">
                        <Spinner animation="border" variant="primary" role="status" />
                        <p className="mt-3">Loading invoice details...</p>
                    </div>
                )}
            </Offcanvas.Body>

            <div className="border-top p-3 d-flex justify-content-end">
                <Button variant="secondary" size="sm" onClick={onHide}>
                    Close
                </Button>
            </div>
        </Offcanvas>
    );
};

export default InvoiceDetailsOffcanvas;
